import React from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { ShieldCheck, QrCode, Waveform, Fingerprint, ArrowRight, Cube } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* NAV */}
      <header className="relative z-30 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size={40} />
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 mono-font text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors duration-200">
              Log in
            </Link>
            <Link to="/register" data-testid="nav-cta-btn" className="px-5 py-2 mono-font text-xs uppercase tracking-[0.2em] bg-[#00FF66] text-black hover:bg-white transition-colors duration-200 border border-[#00FF66] hover:border-white">
              Get Access
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 aura-grid opacity-70" />
        <div className="absolute inset-0 aura-scanline pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 pt-24 pb-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 mono-font text-[10px] uppercase tracking-[0.3em] text-white/60 mb-8">
              <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full glow-green" />
              Patent DTTM-001 · Cryptographic Offline Verification
            </div>
            <h1 className="heading-font text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Ticketing,<br />
              <span className="text-[#00FF66] glow-green">decrypted.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
              AURA — Automated Unified Receipt Architecture. An unlimited multi-tenant
              agentic micro-SaaS for events. Assign inventory, distribute tickets, verify at
              the gate with cryptographic offline hashes and a full append-only audit ledger.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/register" data-testid="hero-cta-btn" className="group inline-flex items-center gap-3 px-6 py-4 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
                Launch Console
                <ArrowRight weight="bold" size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link to="/login" data-testid="hero-secondary-btn" className="inline-flex items-center gap-3 px-6 py-4 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] text-white/80 hover:text-white hover:border-white/60 transition-colors duration-200">
                Access Existing Vault
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl">
              {[
                { label: "COLOR-STATE LIFECYCLE", value: "RED → GREEN → PURPLE" },
                { label: "HASH ALGORITHM", value: "SHA-256 / 16 GROUP" },
                { label: "LEDGER", value: "APPEND-ONLY CHAIN" },
              ].map((s) => (
                <div key={s.label} className="border border-white/10 p-4">
                  <div className="mono-font text-[9px] tracking-[0.25em] uppercase text-white/40">{s.label}</div>
                  <div className="mt-2 mono-font text-xs text-white">{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column - stylized ticket */}
          <div className="lg:col-span-4 relative">
            <div className="relative border border-white/10 bg-[#0A0A0C] p-6 border-glow-green">
              <div className="flex items-center justify-between">
                <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-[#00FF66]">GREEN · ACTIVE</div>
                <Fingerprint size={20} className="text-white/50" />
              </div>
              <div className="mt-6">
                <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-white/40">Event</div>
                <div className="heading-font text-2xl font-bold tracking-tight mt-1">Neon Circuit · Vol. 7</div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-white/40">Bearer</div>
                  <div className="mono-font text-sm mt-1">Zayne Crouse</div>
                </div>
                <div>
                  <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-white/40">Ticket</div>
                  <div className="mono-font text-sm mt-1">AU-N7C1-ZAY-0042</div>
                </div>
              </div>
              <div className="mt-6 border-t border-white/10 pt-6 flex items-center gap-4">
                <div className="w-24 h-24 bg-white flex items-center justify-center">
                  <QrCode size={80} className="text-black" weight="regular" />
                </div>
                <div>
                  <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-white/40">SHA-256 Token</div>
                  <div className="mono-font text-[11px] mt-1 text-[#00FF66] break-all">A9F3E7B21C0D5F84</div>
                  <div className="mono-font text-[10px] mt-3 text-white/40">Offline verifiable</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 border border-[#9D00FF]/40 -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 border border-[#00FF66]/40 -z-10" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="mb-16 max-w-3xl">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Architecture</div>
            <h2 className="heading-font text-3xl sm:text-5xl font-black tracking-tighter mt-3">
              Four autonomous agents. One receipt.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-white/10">
            {[
              {
                icon: Cube,
                title: "Event Orchestration",
                text: "Unlimited multi-tenant event creation with hard ticket caps and AI-generated design.",
                color: "text-[#00FF66]",
              },
              {
                icon: Waveform,
                title: "Inventory Allocation",
                text: "Distribute pools to team members with atomic race-condition-proof counters.",
                color: "text-[#FF9900]",
              },
              {
                icon: Fingerprint,
                title: "Cryptographic Seeds",
                text: "SHA-256 dynamic identity hashes per ticket. Buyer, phone, and timestamps encoded.",
                color: "text-[#9D00FF]",
              },
              {
                icon: ShieldCheck,
                title: "Entry Verification",
                text: "Offline verifiable QR scans with FLASH_GREEN, FLASH_ORANGE, FLASH_RED responses.",
                color: "text-[#FF0033]",
              },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="border-b border-r border-white/10 p-8 transition-colors duration-200 hover:bg-white/[0.02]">
                  <Icon size={28} weight="duotone" className={f.color} />
                  <div className="heading-font text-xl font-bold mt-6 tracking-tight">{f.title}</div>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-24 flex flex-col items-start">
          <h3 className="heading-font text-3xl sm:text-5xl font-black tracking-tighter max-w-3xl">
            Ready for cryptographically sound ticketing?
          </h3>
          <Link to="/register" data-testid="cta-final-btn" className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
            Initialize Vault <ArrowRight weight="bold" size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
            AURA · Automated Unified Receipt Architecture
          </div>
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
            © 2026 Zayne Crouse · Patent DTTM-001
          </div>
        </div>
      </footer>
    </div>
  );
}
